import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Project as ProjectModel } from "models/projects/Project";
import { ApiService } from "services/ApiService";
import { ProjectSearchParams } from "models/projects/ProjectSearchParams";
import { TeamReviewDecision } from "models/projects/TeamReviewDecision";
import ProgressView from "./ProgressView";
import { Form } from "models/forms/Form";
import type { FormSubmissionDto } from 'models/projects/FormSubmission';
import type { UserSubmissionDto } from 'models/projects/UserSubmissionDto';
import { FormSubmission } from 'models/projects/FormSubmission';
import { utilities } from "utils/utilities";
import apiClient from "models/apiClient";

interface Props {
    projectId?: string;
}

const normalizeProject = (p: any): ProjectModel | null => {
    if (!p) return null;

    // create a real Project instance so instance methods (getStatus) exist
    const proj = new ProjectModel();
    proj.projectId = p.projectId ?? proj.projectId;
    proj.userId = p.userId ?? proj.userId;
    proj.teamId = p.teamId ?? proj.teamId;
    proj.workflowId = p.workflowId ?? proj.workflowId;
    proj.name = p.name ?? proj.name;
    proj.description = p.description ?? proj.description;
    // normalize numeric/string status to number
    proj.status = typeof p.status === 'number' ? p.status : Number(p.status ?? proj.status);
    proj.ownerName = p.ownerName ?? proj.ownerName;
    proj.enrollmentSubmissions = Array.isArray(p.enrollmentSubmissions) ? p.enrollmentSubmissions : [];
    proj.team = p.team ?? proj.team;

    return proj;
};

const Project: React.FC<Props> = ({ projectId }) => {

    const { artistId, workflowId, projectIdRoute } = useParams<{ artistId?: string; workflowId?: string; projectIdRoute?: string }>();
    const [project, setProject] = useState<ProjectModel | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [isOwner, setIsOwner] = useState<boolean>(false);
    const [isTeamMember, setIsTeamMember] = useState<boolean>(false);
    const [isEditingDescription, setIsEditingDescription] = useState<boolean>(false);
    const [editedDescription, setEditedDescription] = useState<string>('');
    const [isEditingName, setIsEditingName] = useState<boolean>(false);
    const [editedName, setEditedName] = useState<string>('');
    const [isCompletingProject, setIsCompletingProject] = useState<boolean>(false);
    const userId = utilities.getUserId();

    // Check if all enrollment submissions have been completed/reviewed
    const areAllReviewsComplete = project ? 
        project.enrollmentSubmissions.length > 0 && 
        project.enrollmentSubmissions.every(es => es.userSubmission !== null) 
        : false;

    useEffect(() => {
        const fetchProject = async () => {
            try {
                if (projectId) {
                    const response = await ApiService.getProject(projectId);
                    if (response.success && response.data) {
                        const proj = normalizeProject(response.data);
                        setProject(proj);
                    }
                } else {
                    const searchModel: ProjectSearchParams = {
                        artistId: artistId || "",
                        workflowId: workflowId || "",
                        projectId: projectIdRoute || "",
                        createPartialProject: true,
                        teamId: ""
                    };
                    //console.log("Searching for project with params:", searchModel);
                    const response = await ApiService.getProjectsBySearch(searchModel);

                    if (response.success && response.data && response.data?.length > 0) {

                        const projects = response.data.filter(p => {
                            const status = typeof p.status === 'string' ? parseInt(p.status, 10) : p.status;
                            return status === TeamReviewDecision.InProgress
                                || status === TeamReviewDecision.NotYetStarted
                                || status === TeamReviewDecision.WaitingForUserSubmission;
                        });
                        if (projects.length > 0) {
                            const proj = normalizeProject(projects[0]);
                            setProject(proj);
                        }
                    }
                }
            } catch (error) {
                console.error("Error fetching project:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchProject();
    }, [projectId]);

    // compute ownership and membership whenever project or userId changes
    useEffect(() => {
        if (!project) {
            setIsOwner(false);
            setIsTeamMember(false);
            return;
        }
        const isOwner = userId === project.userId;
        setIsOwner(isOwner);
        const isMember = project.team?.teammates?.some((m: any) => m.userId === userId || m.id === userId);
        setIsTeamMember(!!isMember);
    }, [project, userId]);

    const handleEditName = () => {
        setEditedName(project?.name || '');
        setIsEditingName(true);
    };

    const handleSaveName = async () => {
        if (!project) return;
        
        try {
            const response = await apiClient.put(`/Projects/${project.projectId}`, { 
                name: editedName 
            });
            
            if (response.data.success) {
                setProject(prev => {
                    if (!prev) return prev;
                    const updated = new ProjectModel();
                    Object.assign(updated, prev);
                    updated.name = editedName;
                    return updated;
                });
                setIsEditingName(false);
            } else {
                console.error('Failed to update name:', response.data.errors);
            }
        } catch (error) {
            console.error('Error updating name:', error);
        }
    };

    const handleCancelNameEdit = () => {
        setIsEditingName(false);
        setEditedName('');
    };

    const handleEditDescription = () => {
        setEditedDescription(project?.description || '');
        setIsEditingDescription(true);
    };

    const handleSaveDescription = async () => {
        if (!project) return;
        
        try {
            // Update description via API
            const response = await ApiService.updateProjectNameAndDescription(project.projectId, project.name, editedDescription);
            
            setProject(prev => {
                if (!prev) return prev;
                const updated = new ProjectModel();
                Object.assign(updated, prev);
                updated.description = editedDescription;
                return updated;
            });
            setIsEditingDescription(false);
        } catch (error) {
            console.error('Error updating description:', error);
        }
    };

    const handleCancelEdit = () => {
        setIsEditingDescription(false);
        setEditedDescription('');
    };

    const handleMarkAsCompleted = async () => {
        if (!project) return;
        
        setIsCompletingProject(true);
        try {
            // Update project status to Completed (2)
            const response = await apiClient.put(`/Projects/${project.projectId}/projectstatus`, { status: 2 });
            
            if (response.data.success) {
                // Update local project state
                setProject(prev => {
                    if (!prev) return prev;
                    const updated = new ProjectModel();
                    Object.assign(updated, prev);
                    updated.status = 2 as any; // Completed
                    return updated;
                });
            } else {
                console.error('Failed to update project status:', response.data.errors);
            }
        } catch (error) {
            console.error('Error marking project as completed:', error);
        } finally {
            setIsCompletingProject(false);
        }
    };

    const handleSubmit = async (submissionDto: UserSubmissionDto) => {
        // Convert base UserSubmissionDto into appropriate specific DTO here
        const submission = submissionDto.userSubmission;
        if (submission.type === 'FormSubmission') {
            const formSubmission = submission as FormSubmission;
            const dto: FormSubmissionDto = {
                workflowId: submissionDto.workflowId,
                nodeId: submissionDto.nodeId,
                projectId: submissionDto.projectId,
                formId: formSubmission.formId,
                formSubmission: formSubmission,
                type: "FormSubmission"
            };
            const response = await ApiService.submitFormSubmission(dto);

            if (response.success && response.data){
                console.log("Form submission successful:", response.data);
                // response.data expected to be the created/updated UserSubmission (or FormSubmission)
                const returnedSubmission = response.data as any;

                setProject((prevProject) => {
                    if (!prevProject) return prevProject;
                    // create a shallow copy of the project and its enrollmentSubmissions
                    const updated = { ...prevProject } as ProjectModel;
                    updated.enrollmentSubmissions = (updated.enrollmentSubmissions || []).map(es => {
                        // match on nodeId
                        if (es.nodeId === dto.nodeId) {
                            return { ...es, userSubmission: returnedSubmission } as any;
                        }
                        return es;
                    });
                    return updated;
                });
            }
        } else {
            console.warn('Unhandled submission type:', submission.type);
        }
    };

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {loading && (
                <div className="space-y-6 animate-pulse" aria-live="polite" aria-busy="true">
                    <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-6">
                        <div className="h-6 bg-gray-200 rounded w-1/3 mx-auto mb-4" />
                        <div className="space-y-2">
                            <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto" />
                            <div className="h-4 bg-gray-200 rounded w-2/3 mx-auto" />
                        </div>
                        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="h-20 bg-gray-100 rounded-lg" />
                            <div className="h-20 bg-gray-100 rounded-lg" />
                        </div>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-6">
                        <div className="h-5 bg-gray-200 rounded w-1/4 mx-auto mb-4" />
                        <div className="h-40 bg-gray-100 rounded" />
                    </div>
                </div>
            )}

            {!loading && !project && (
                <div className="text-center text-gray-600">Project not found.</div>
            )}

            {project && (
                <div className="space-y-6">
                    <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-6" aria-label="Project summary">
                        <div className="flex flex-col items-center text-center gap-4">
                            <div className="flex items-center gap-3 flex-wrap justify-center">
                                {isEditingName ? (
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            value={editedName}
                                            onChange={(e) => setEditedName(e.target.value)}
                                            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-2xl font-semibold text-gray-900"
                                            placeholder="Project name..."
                                        />
                                        <button
                                            onClick={handleSaveName}
                                            className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                                        >
                                            Save
                                        </button>
                                        <button
                                            onClick={handleCancelNameEdit}
                                            className="px-3 py-1 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 text-sm font-medium"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2 group">
                                        {isTeamMember && (
                                            <button
                                                onClick={handleEditName}
                                                className="opacity-0 group-hover:opacity-100 px-2 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-all"
                                                title="Edit name"
                                            >
                                                ✏️
                                            </button>
                                        )}
                                        <h1 className="text-2xl font-bold text-gray-900">{project && (project.name.length > 0 ? project.name : `${project.ownerName}'s Project`)}</h1>
                                    </div>
                                )}
                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${(() => {
                                    const status = typeof project.status === 'number' ? project.status : Number(project.status ?? -1);
                                    if (status === 0) return 'bg-green-100 text-green-800';
                                    if (status === 1) return 'bg-red-100 text-red-800';
                                    if (status === 2) return 'bg-blue-100 text-blue-800';
                                    return 'bg-gray-100 text-gray-800';
                                })()}`}>
                                    {project.getStatus()}
                                </span>
                            </div>

                            {(() => {
                                const status = typeof project.status === 'number' ? project.status : Number(project.status ?? -1);
                                return status === 1 && (project as any).denialReason && (
                                    <div className="w-full max-w-3xl bg-red-50 border border-red-200 rounded-lg p-4">
                                        <p className="text-sm font-medium text-red-800 mb-1">Denial Reason:</p>
                                        <p className="text-sm text-red-700">{(project as any).denialReason}</p>
                                    </div>
                                );
                            })()}

                            <div className="w-full max-w-3xl">
                                {isEditingDescription ? (
                                    <div className="space-y-2">
                                        <textarea
                                            value={editedDescription}
                                            onChange={(e) => setEditedDescription(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
                                            placeholder="Enter project description..."
                                        />
                                        <div className="flex gap-2 justify-center">
                                            <button
                                                onClick={handleSaveDescription}
                                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                                            >
                                                Save
                                            </button>
                                            <button
                                                onClick={handleCancelEdit}
                                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 text-sm font-medium"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="relative group">
                                        <div className="flex items-center gap-2">
                                            {isTeamMember && (
                                                <button
                                                    onClick={handleEditDescription}
                                                    className="opacity-0 group-hover:opacity-100 px-2 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-all"
                                                    title="Edit description"
                                                >
                                                    ✏️
                                                </button>
                                            )}
                                            <p className="text-gray-600">
                                                {project.description || (isTeamMember ? 'No description yet. Click to add one.' : 'No description available.')}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Show complete button if team member and all reviews are done and not already completed */}
                        {isTeamMember && areAllReviewsComplete && ((() => {
                            const status = typeof project.status === 'number' ? project.status : Number(project.status ?? -1);
                            return status !== 2; // Not already completed
                        })()) && (
                            <div className="mt-6 pt-6 border-t border-gray-200">
                                <div className="flex flex-col items-center gap-2">
                                    <p className="text-sm text-gray-600">All reviews are complete. Ready to mark this project as completed?</p>
                                    <button
                                        onClick={handleMarkAsCompleted}
                                        disabled={isCompletingProject}
                                        className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium transition-colors"
                                    >
                                        {isCompletingProject ? 'Marking as Completed...' : '✓ Mark Project as Completed'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-6" aria-label="Project progress">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Progress</h3>
                        <div className="flex justify-center">
                            <ProgressView
                                className="w-full"
                                enrollmentSubmissions={project.enrollmentSubmissions}
                                onSubmit={handleSubmit}
                                projectId={project.projectId || utilities.DEFAULT_GUID}
                                workflowId={project.workflowId}
                                isOwner={isOwner}
                                isTeamMember={isTeamMember}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default Project;